import type { RouteRecordRaw } from "vue-router";

// Таблица маршрутов отдельно от index.ts: там createWebHistory, которому
// нужен window - а эту таблицу проверяют node-тесты (routes.test.ts).

// This router exists purely to keep the URL/browser history in sync with
// the app's tab/section state - App.vue, ProfileView.vue, LiveView.vue and
// AdminShell.vue read useRoute()/useRouter() directly and drive their own
// v-show'd panels; nothing here is rendered via <router-view>, so no route
// needs a `component`. Map/Live/Explore/Admin panels all stay mounted once
// behind v-show (MapLibre/HLS instances would otherwise get torn down and
// recreated on every tab switch).
// Vue Router's RouteRecordRaw type requires one of component/components/
// children/redirect on every entry - none of these routes render via
// <router-view> (see comment above), so the component-less ones need an
// explicit empty `children: []` to satisfy RouteRecordSingleViewWithChildren
// rather than actually declaring any nested routes.
export const routes: RouteRecordRaw[] = [
  { path: "/", redirect: "/map" },
  { path: "/map", name: "map", children: [] },
  // Открытый маршрут: /route/<slug> (код routes.slug, не id). Вкладка - «Маршруты»
  // (tabFromPath не знает "route" и берёт map). Старые /map?route=<id> App.vue
  // сам заменяет на этот вид.
  { path: "/route/:slug", name: "route", children: [] },
  { path: "/live", name: "live", children: [] },
  // Каталог - режим «Список» вкладки «Маршруты» (/map?view=list), не
  // отдельная вкладка. Старые ссылки /routes и /explore ведут туда.
  { path: "/routes", redirect: { path: "/map", query: { view: "list" } } },
  { path: "/explore", redirect: { path: "/map", query: { view: "list" } } },
  { path: "/safety", name: "safety", children: [] },
  // Безопасность переехала из профиля в отдельную вкладку.
  { path: "/profile/safety", redirect: "/safety" },
  // Голый /profile - не редирект: раздел по умолчанию выбирает ProfileView
  // (notifications при непрочитанных, иначе collection), см. useProfileDefaultSection.
  { path: "/profile/:section?", name: "profile", children: [] },
  { path: "/admin", redirect: "/admin/routes" },
  { path: "/admin/:section", name: "admin", children: [] },
  // Standalone POC tool, not part of the tab shell at all (see App.vue's
  // isImportPointsRoute) - own minimal layout, no map/live/explore/profile
  // chrome, nothing here persists to the backend.
  { path: "/tools/import-points", name: "importPoints", children: [] },
  // MikroTik Hotspot captive-portal landing page (Мынжылкы) - own minimal
  // layout, same standalone treatment as the routes above. Reads the
  // router's redirect query params (mac/ip/link-login-only/link-orig/error)
  // to build the actual login POST - see CaptiveView.vue.
  { path: "/captive", name: "captive", children: [] },
  // Telegram Mini App: регистрация на маршрут из бота (кнопка /register и
  // меню бота) - тот же standalone-приём, что и у captive выше, без оболочки
  // вкладок; авторизация по initData, не сессией сайта - см. TgRegisterView.vue.
  { path: "/tg/register", name: "tgRegister", children: [] },
  // Public profile page (ecoway.kz/user/username) - namespaced under a
  // fixed /user/ prefix (moved 2026-08-14 from a bare top-level
  // /:username) specifically so a username can never collide with a
  // present-or-future top-level section name (/map, /admin, whatever comes
  // next) - the reserved-word list (constants/reservedUsernames.ts) still
  // applies at username-set time for other reasons (impersonation risk
  // etc.), but no longer needs to track every route this file defines.
  // Не отдельная страница: панель профиля поверх карты (tabFromPath -> map),
  // см. App.vue profileUsername / UserProfilePanel.vue.
  { path: "/user/:username", name: "publicProfile", children: [] },
  // "Поделиться" link off an active route registration (see App.vue's
  // isHikeCardRoute / ProfileView.vue's shareActiveRegistration) - public,
  // no auth, meant for family/friends without an account.
  { path: "/hike/:token", name: "hikeCard", children: [] },
  // Публичный архив обновлений и страницы документов (changelog/новости/
  // юридические) - вне оболочки вкладок, как /hike (см. App.vue).
  { path: "/news", name: "news", children: [] },
  { path: "/news/:slug", name: "newsDocument", children: [] },
  // Единая форма входа для всех приложений EcoWay (Регистратор,
  // mediamtx-connect): /login?return=<url> - см. LoginPageView.vue.
  { path: "/login", name: "login", children: [] },
  // Email-регистрация/сброс пароля - ссылка из письма ведёт прямо сюда
  // (backend/app/auth/service.py собирает её из BACKEND_PUBLIC_APP_URL).
  { path: "/verify-email", name: "verifyEmail", children: [] },
  { path: "/reset-password", name: "resetPassword", children: [] },
  // Прямая ссылка на модалку "Следующий твой" #ПриветEcoway - печатается на
  // QR-кодах на локации. App.vue ловит route.name, открывает модалку
  // (store/helloFeedWave) и нормализует URL на /live.
  { path: "/hello/join", name: "helloJoin", children: [] },
  // OAuth-редиректы (Google/Telegram OIDC) - должны совпадать буквально с
  // frontendRedirectPath в composables/useOAuthLogin.ts и с Allowed URL/
  // Authorized redirect URI, зарегистрированным у провайдера.
  { path: "/auth/google/callback", name: "googleCallback", children: [] },
  { path: "/auth/telegram/callback", name: "telegramOidcCallback", children: [] },
  { path: "/:pathMatch(.*)*", redirect: "/map" },
];
